/**
 * Conversion between the screening result, the exchange JSON file and CRM contact fields.
 * Screening export, CRM JSON import and "save to CRM" all go through these two functions.
 */
import { CATEGORIES, CategoryNotes, CategoryScores, ContactKind } from "./types";
import { clampScore, computeFinalScore } from "./finalScore";

export interface CrmImport {
    fullname: string;
    email: string;
    phone: string;
    kind: ContactKind;
    scores: CategoryScores;
    notes: CategoryNotes;
}

const KINDS: ContactKind[] = ["Osoba", "Společnost", "Lead"];

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

/** Builds the exchange JSON from a screening result (or any object with the same shape). */
export function toCrmImport(source: any): CrmImport {
    const scores = {} as CategoryScores;
    const notes = {} as CategoryNotes;
    for (const c of CATEGORIES) {
        scores[c] = clampScore(source?.scores?.[c]);
        notes[c] = asText(source?.notes?.[c]);
    }
    return {
        fullname: asText(source?.fullname).trim(),
        email: asText(source?.email).trim(),
        phone: asText(source?.phone).trim(),
        kind: KINDS.includes(source?.kind) ? source.kind : "Lead",
        scores,
        notes,
    };
}

/** Maps the exchange JSON to CRM contact fields (`analysis_<category>_score/text`). */
export function fromCrmImport(json: unknown): Record<string, string | number> {
    if (!json || typeof json !== "object" || Array.isArray(json)) {
        throw new Error("Invalid import file: expected a JSON object");
    }
    const data = toCrmImport(json);
    const fields: Record<string, string | number> = {
        fullname: data.fullname,
        email: data.email,
        phone: data.phone,
        kind: data.kind,
    };
    for (const c of CATEGORIES) {
        fields[`analysis_${c}_score`] = data.scores[c];
        fields[`analysis_${c}_text`] = data.notes[c];
    }
    fields.score_final = computeFinalScore(CATEGORIES.map((c) => data.scores[c]));
    return fields;
}
